import { Breadcrumbs, BreadcrumbTemplateEnum, Page, PageInner, PageSection, SearchBox } from "@app/shared";
import { QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import React, { JSX } from "react";
import { Link } from "react-router-dom";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import {
  ManageResourceTagDisplayModel,
  Perspectives,
  ResourceTagFilterDropdownItems,
  ResourceTagViewerProps,
  ResourceTagViewerState
} from "@app/tags/types";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { RoleRestricted, Roles, UserContext, UserContextProps } from "@app/user";
import { Modal } from "@app/shared/Modals";
import { FlyoutLink } from "@app/shared/Flyouts";
import ResourceTagPanel from "@app/tags/ResourceTagPanel";

type ResourceTagViewerWrappedProps = ResourceTagViewerProps & UserContextProps;

class ResourceTagViewer extends React.Component<ResourceTagViewerWrappedProps, ResourceTagViewerState> {
  private title = "Tag Management";
  private resourceTaggingService = new ResourceTaggingService();

  // React Built-Ins
  constructor(props: ResourceTagViewerWrappedProps) {
    super(props);
    this.state = {
      deleteConfirmationModalIsOpen: false,
      loading: false,
      perspective: this.props.perspective || Perspectives.Group,
      resourceTags: [],
      searchValue: this.props.searchValue || "",
      selectedRows: [],
      showXRows: this.props.showXRows || this.props.take || 100,
      tableRows: [],
      take: this.props.take || 100
    };
  }

  public componentDidMount(): void {
    const { perspective } = this.state;
    this.handleViewBySwitch(ResourceTagFilterDropdownItems.find((item) => item.key === perspective));
  }

  public render() {
    const { loading, perspective, resourceTags, tableRows, showXRows } = this.state;
    const selectedKeys: string[] = [...new Set(this.state.selectedRows.map((sr: ManageResourceTagDisplayModel) => sr.key))];
    const groupName: Perspectives = ResourceTagFilterDropdownItems.find((item) => item.key === perspective).key;
    const numRecs: number = selectedKeys.length;

    return (
      <Page documentTitle={this.title}>
        <Breadcrumbs template={BreadcrumbTemplateEnum.Tags} items={[]}/>
        <PageInner>
          <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
            <h1 className="page-title mt-0 mb-0">{this.title}</h1>
            {resourceTags.length != 0 &&
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <QESButton style={{ marginLeft: "auto", marginRight: 5 }} qesTestId={"resource-tag-viewer__download-tags-button"}
                           onClick={() => this.downloadTags()}>Download Existing Tags</QESButton>
              </RoleRestricted>
            }
            <Link to="./import"><QESButton qesTestId={"resource-tag-viewer__upload-tags-button"}>Upload Tags</QESButton></Link>
          </header>
          <PageSection>
            {resourceTags.length != 0 &&
              <SearchBox
                fieldsetClass="d-flex"
                inputLabel={"Search"}
                descriptiveText={this.buildSearchDescriptor()}
                onSubmit={this.handleSearch}
                testId="resource-tag-viewer__search"
                alwaysShowClearButton={true}
              />
            }
            <div className="mt-3 container-sm ml-0">
              <div className="d-flex justify-content-between">
                <div className="flex-grow-0">
                  {resourceTags.length != 0 &&
                    <NewActionMenu
                      buttonClassNames={"qes-button-secondary"}
                      buttonText={`View by: ${ResourceTagFilterDropdownItems.find((item) => item.key === perspective).name}`}
                      key={perspective}
                      testId="resource-tag-viewer__view-by-dropdown"
                    >
                      {ResourceTagFilterDropdownItems.map((item) => {
                        const isSelected = item.key === perspective;
                        return (
                          <NewActionMenuItem
                            display={item.name}
                            isSelected={isSelected}
                            key={item.key}
                            onClick={() => this.handleViewBySwitch(item)}
                            testId={`resource-tag-viewer__view-by-option--${item.key}`}
                          />
                        )
                      })}
                    </NewActionMenu>

                  }
                </div>
                {this.props.user.email.endsWith("questanalytics.com") &&
                  <div>
                    <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                      <FlyoutLink
                        content={{
                          body: <ResourceTagPanel/>,
                          title: "Tags",
                        }}
                        testId="resource-tag-review-flyout-link"
                        size="40"
                        classNames="qes-button-secondary p-0"
                        onClose={() => {
                          const { perspective } = this.state;
                          this.handleViewBySwitch(ResourceTagFilterDropdownItems.find((item) => item.key === perspective));
                        }}
                      >
                        Add Tags
                      </FlyoutLink>
                      <QESButton qesType="secondary-danger" qesTestId='delete-button' onClick={() => this.showModal()}
                                 disabled={numRecs === 0}>
                        Delete Tags
                      </QESButton>
                    </RoleRestricted>
                  </div>
                }
              </div>
              <div>&nbsp;</div>
              <Modal
                isVisible={this.state.deleteConfirmationModalIsOpen}
                content={{
                  body: "Are you sure you want to delete the selection?",
                  cancelButtonText: "Cancel",
                  confirmButtonAction: () => this.deleteChecked(groupName, selectedKeys.map(Number)),
                  confirmButtonText: "Yes",
                  title: "Delete"
                }}
                closeModal={() => this.hideModal()}
              />
              <Waffles<ManageResourceTagDisplayModel>
                columns={this.buildColumnConfig()}
                customSort={this.handleCustomSort}
                data={tableRows.slice(0, showXRows)}
                emptyTableMessage={"No Tags Available"}
                loadMore={this.handleLoadMore}
                fullWidth={true}
                loading={loading}
                passThroughSorting={true}
                showCheckboxes={true}
                selectedKeys={selectedKeys}
                handleCheckboxChange={this.handleCheckboxChange}
                testId="resource-tag-viewer__tags-waffles"
              />
            </div>
          </PageSection>
        </PageInner>
      </Page>
    );
  }

  public deleteChecked = async (groupName: Perspectives, selectedKeys: number[]): Promise<void> => {
    this.setState({
      loading: true,
      selectedRows: []
    });
    this.hideModal();
    await this.delete(groupName, selectedKeys);
  }

  public handleCheckboxChange = (selectedKeys: string[]): void => {
    const data = this.state.tableRows.slice(0, this.state.showXRows);
    const selectedRows: ManageResourceTagDisplayModel[] = data.filter((p) => selectedKeys.includes(p.key));
    this.setState({
      selectedRows: selectedRows
    });
  }

  private showModal = (): void => {
    this.setState({
      deleteConfirmationModalIsOpen: true,
    });
  }

  private hideModal = (): void => {
    this.setState({
      deleteConfirmationModalIsOpen: false,
    });
  }

  public delete = async (typeName: Perspectives, selectedKeys: number[]): Promise<void> => {
    const data = this.state.tableRows.slice(0, this.state.showXRows);
    const associatedKeys = data.filter((p => selectedKeys.map(key => key + '').includes(p.key)))
      .flatMap(p => p.resourceTagIds)
      .map(key => parseInt(key));
    await this.resourceTaggingService.deleteTags(associatedKeys);
    this.getResourceTags(typeName);
  }

  public downloadTags(): void {
    this.resourceTaggingService.downloadTags()
      .then((data) => {
        const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "downloadtags.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  // Getters
  private getResourceTags = (targetPerspective: string) => {
    this.setState({ loading: true, tableRows: [] });
    const { perspective } = this.state;

    this.resourceTaggingService.getClientProjectTagInfo(targetPerspective as Perspectives ?? perspective as Perspectives).then((response: ManageResourceTagDisplayModel[]) => {
      const tempResourceTags: ManageResourceTagDisplayModel[] = response.map(r => convertResourceTagModelToRow(r));
      this.setState({ loading: false, perspective: targetPerspective, resourceTags: tempResourceTags, tableRows: tempResourceTags });
    });

    function convertResourceTagModelToRow(resourceTagModel: ManageResourceTagDisplayModel): ManageResourceTagDisplayModel {
      return {
        ...resourceTagModel,
        [wafflesKeyColumnName]: resourceTagModel.key.toString(),
      };
    }
  };

  // Builders
  private buildColumnConfig = (): ColumnConfig => {
    const { perspective } = this.state;
    const showGroupCols = perspective == Perspectives.Group;
    const showTagCols = perspective == Perspectives.Tag;
    const showProjectCols = perspective == Perspectives.Project;
    return {
      /* eslint-disable sort-keys */
      tagGroupName: {
        displayText: "Group Name",
        hidden: !showGroupCols,
        sortOrder: 1,
        testId: "resource-tag-viewer__tags-waffles__group-name-column",
      },
      tagName: {
        displayText: "Tag Name",
        hidden: !(showTagCols || showGroupCols),
        sortOrder: 2,
        testId: "resource-tag-viewer__tags-waffles__tag-name-column",
      },
      projectName: {
        displayText: "Project Name",
        hidden: !showProjectCols,
        sortOrder: 3,
        testId: "resource-tag-viewer__tags-waffles__project-count-column",
      },
      templateName: {
        displayText: "Template Name",
        hidden: !showProjectCols,
        sortOrder: 4,
        testId: "resource-tag-viewer__tags-waffles__template-name-column",
      },

      projectCount: {
        displayText: "Number of Projects",
        hidden: !(showGroupCols || showTagCols),
        sortOrder: 5,
        testId: "resource-tag-viewer__tags-waffles__project-count-column",
      },
      groupCount: {
        displayText: "Number of Groups",
        hidden: !showTagCols,
        sortOrder: 7,
        testId: "resource-tag-viewer__tags-waffles__group-count-column",
      },
      tagCount: {
        displayText: "Number of Tags",
        hidden: !(showProjectCols || showGroupCols),
        sortOrder: 6,
        testId: "resource-tag-viewer__tags-waffles__tag-count-column",
      },
      /* eslint-enable sort-keys */
    };
  }

  private buildSearchDescriptor = (): string => {
    const { perspective } = this.state;
    const params = ResourceTagFilterDropdownItems.find((item) => item.key === perspective).name;

    return `This will search each row's ${params} name.`;
  }

  // Handlers
  private handleCustomSort = (rows: ManageResourceTagDisplayModel[], column: keyof ManageResourceTagDisplayModel, direction: SortDirections): ManageResourceTagDisplayModel[] => {
    this.setState({ loading: true });
    const { searchValue, tableRows } = this.state;
    rows = rows ?? tableRows;

    if (direction === SortDirections.None) {
      this.handleSearch(searchValue);
      return;
    } else {
      const multiplier = direction === SortDirections.Asc ? 1 : -1;
      const sorted = [...rows].sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      this.setState({ loading: false, tableRows: sorted });
      return sorted;
    }
  }

  private handleLoadMore = (justChecking: boolean): boolean => {
    const { tableRows, showXRows, take } = this.state;
    let targetRows = showXRows;
    if (!justChecking) {
      targetRows += take;
      this.setState({ showXRows: targetRows });
    }
    return tableRows.length > targetRows;
  }

  private handleSearch = (searchValue: string) => {
    this.setState({ loading: true });
    const { perspective, resourceTags, take } = this.state;

    const newRows: ManageResourceTagDisplayModel[] = [];
    resourceTags.forEach((r) => {
      let searchFields: string[];
      switch (perspective) {
        case Perspectives.Group:
          searchFields = [r.tagGroupName, r.tagName];
          break;
        case Perspectives.Tag:
          searchFields = [r.tagName];
          break;
        case Perspectives.Project:
          searchFields = [r.projectName, r.templateName];
          break;
      }
      if (searchFields.some(x => !!x && x.toLowerCase().includes(searchValue.toLowerCase())))
        newRows.push(r);
    });
    this.setState({ loading: false, searchValue: searchValue, showXRows: take, tableRows: newRows });
  }

  private handleViewBySwitch = (tempSelectedItem: DropdownItem<string>): void => {
    const { take } = this.state;
    this.setState({ perspective: tempSelectedItem.key, searchValue: "", showXRows: take });
    this.getResourceTags(tempSelectedItem.key);
    $("#resource-tag-viewer__search__input").val("");
  }
}

const WrappedResourceTagViewer = (props: ResourceTagViewerProps): JSX.Element => {
  const user = React.useContext(UserContext);
  return <ResourceTagViewer {...props} user={user}/>;
}

export default WrappedResourceTagViewer;